import { detectDevice } from 'Utils';
import type { DeviceKind } from './Constants';

export type DeviceClass = 'phone' | 'tablet' | 'desktop' | 'unknown';
export type RouteLayout = 'auth' | 'app' | 'other';
export type OrientationMode = 'portrait' | 'landscape' | 'system';

export const AUTH_ROUTE_PREFIXES = [
  '/signin',
  '/register',
  '/verify',
  '/welcome',
  '/trial-welcome',
  '/sound-output',
];

export const APP_LANDSCAPE_ROUTE_PREFIXES = [
  '/roadmap',
  '/tutorial',
  '/lesson',
  '/settings',
  '/repertoire',
  '/repertoire-play',
  '/dashboard',
  '/history-of-piano-music',
  '/plan',
];

const getUserAgent = () => {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
};

const matchesRoutePrefix = (pathname: string, prefix: string) => {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

const getRouteSegment = (pathname: string) => {
  const normalizedPathname = pathname.replace(/^\/+/, '');
  const [segment] = normalizedPathname.split('/');
  return segment || 'root';
};

export const getRouteLayout = (pathname: string): RouteLayout => {
  if (AUTH_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix))) {
    return 'auth';
  }
  if (APP_LANDSCAPE_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix))) {
    return 'app';
  }
  return 'other';
};

export const getDeviceClass = (deviceKind: DeviceKind = detectDevice()): DeviceClass => {
  if (deviceKind === 'iphone') return 'phone';
  if (deviceKind === 'ipad') return 'tablet';
  if (deviceKind === 'desktop' || deviceKind === 'web') return 'desktop';
  if (deviceKind === 'android') {
    const ua = getUserAgent();
    // Android tablets usually omit "Mobile" in the UA string.
    return /Mobile/i.test(ua) ? 'phone' : 'tablet';
  }
  return 'unknown';
};

export const getOrientationModeForRoute = (pathname: string): OrientationMode => {
  const routeLayout = getRouteLayout(pathname);
  if (routeLayout === 'auth') return 'portrait';
  if (routeLayout === 'app') return 'landscape';
  return 'system';
};

// Routes that opt out of the global landscape lock and may rotate freely.
// The repertoire PLAY page and the repertoire browser (`/repertoire`) are both
// exempt — the browser's piece list reflows fine in portrait, and its sidebar
// collapses to a drawer on narrow widths (see Menu `collapseToDrawer`).
// Note: `/repertoire` does not match `/repertoire-play` (prefix check requires
// an exact segment or a trailing slash), so the play page keeps its own entry.
export const ORIENTATION_FREE_ROUTE_PREFIXES = ['/repertoire-play', '/repertoire'];

// Largest iPad (iPad Pro 12.9"/13") measures 1366 logical points on its longest
// edge. Devices at or below this — phones and every iPad — are subject to the
// landscape lock; anything larger (laptops/desktops) is left unrestricted.
export const MAX_LOCKABLE_SCREEN_EDGE = 1366;

// Smallest iPad mini measures 1024 logical points on its longest edge. The
// largest phones top out around 932 points, so this cleanly separates tablets
// from phones. Free rotation on the exempt routes is allowed only at or above
// this size; smaller (phone) screens stay locked to landscape everywhere.
export const MIN_FREE_ROTATION_SCREEN_EDGE = 1024;

const getLongestScreenEdge = (): number => {
  if (typeof window === 'undefined' || !window.screen) return 0;
  return Math.max(window.screen.width || 0, window.screen.height || 0);
};

export const isOrientationLockEligibleScreen = (): boolean => {
  const longestEdge = getLongestScreenEdge();
  if (!longestEdge) return false;
  return longestEdge <= MAX_LOCKABLE_SCREEN_EDGE;
};

// True only on iPad-mini-sized-or-larger screens (i.e. tablets, not phones),
// where it is reasonable to let the exempt routes rotate freely.
export const isFreeRotationEligibleScreen = (): boolean => {
  const longestEdge = getLongestScreenEdge();
  if (!longestEdge) return false;
  return longestEdge >= MIN_FREE_ROTATION_SCREEN_EDGE;
};

// Orientation lock applied when the device is Capacitor-native and the screen is
// iPad-sized or smaller. Auth routes rotate freely on tablets so iPadOS can use
// the device's current orientation, while phones keep the compact portrait auth
// layout. The repertoire routes above also rotate freely on tablets; other
// routes remain landscape-only.
export const getOrientationLockForRoute = (
  pathname: string,
  allowFreeRotation: boolean,
): OrientationMode => {
  const routeLayout = getRouteLayout(pathname);
  if (routeLayout === 'auth') return allowFreeRotation ? 'system' : 'portrait';
  if (
    allowFreeRotation &&
    ORIENTATION_FREE_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(pathname, prefix))
  ) {
    return 'system';
  }
  return 'landscape';
};

export const syncResponsiveDataAttributes = (pathname: string, deviceClass: DeviceClass) => {
  if (typeof document === 'undefined') return;
  const routeLayout = getRouteLayout(pathname);
  document.documentElement.setAttribute('data-device-class', deviceClass);
  document.documentElement.setAttribute('data-route-layout', routeLayout);
  document.documentElement.setAttribute('data-route-segment', getRouteSegment(pathname));
};
